import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FieldRenderProps } from 'react-final-form';
import classNames from 'classnames';
import { VERIFIED_PIN_ID } from 'data/global';

import './PinField.styles.scss';

type ExtractPinFieldProps = FieldRenderProps<string, HTMLElement>;

interface BackspaceKeyEvent extends React.KeyboardEvent<HTMLInputElement> {
  key: 'Backspace';
}

const digitArray = (length: number) => Array.from({ length }).map((_) => '');

type PinFieldProps = {
  className?: string;
  numOfPins: number;
  name?: string;
  onChange?: (value: string) => void;
} & ExtractPinFieldProps;

const PinField: React.FC<PinFieldProps> = (props) => {
  const {
    input,
    meta: { touched, error },
    className,
    numOfPins,
    name,
    onChange,
    ...rest
  } = props;

  const classes = classNames('pin-field', className);

  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [numbers, setNumbers] = useState<Array<string>>(digitArray(numOfPins));

  const deletingAtm = useRef<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const hasError = useMemo(() => touched && error, [error, touched]);

  const handleOnKeyDown = useCallback(
    ({ key }: BackspaceKeyEvent, index: number) => {
      if (key !== 'Backspace') {
        deletingAtm.current = false;
        return;
      }

      // Nothing to clear here, so the keystroke steps back instead - no change
      // event follows an already empty box.
      if (numbers[index] === '') {
        setActiveIndex((oldIndex) => Math.max(0, oldIndex - 1));
      }

      deletingAtm.current = true;
    },
    [numbers],
  );

  const handleOnChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>, numIndex: number) => {
      const {
        target: { value },
      } = event;

      if (/[^0-9]/.test(value)) return;

      if (numbers.length < numOfPins) return;

      setActiveIndex((oldIndex) => {
        // Backspace on a filled box only clears it. Stepping back is for an
        // already empty box, which handleOnKeyDown does before this fires.
        if (deletingAtm.current) return oldIndex;

        return Math.min(oldIndex + 1, numOfPins - 1);
      });

      setNumbers((oldNumbers) => {
        // The box is controlled but not length capped, so typing into a filled
        // one arrives as both digits - keep the one just typed, whichever side
        // of the old digit the caret sat on.
        const previous = oldNumbers[numIndex];
        const digit =
          value.length > 1 && value.charAt(0) === previous
            ? value.charAt(1)
            : value.charAt(0);

        return oldNumbers.map((number, index) =>
          index === numIndex ? digit : number,
        );
      });
    },
    [numOfPins, numbers.length],
  );

  useEffect(() => {
    (input ? input.onChange : onChange)?.(numbers.join(''));
  }, [numbers, input, onChange]);

  useEffect(() => {
    if (!inputRef.current) return;
    inputRef.current.focus();
  }, [numbers, activeIndex]);

  return (
    <div className={classes}>
      <div className="pin-field__content" {...rest}>
        {Array.from({ length: numOfPins }).map((_, index) => {
          return (
            <input
              ref={index === activeIndex ? inputRef : null}
              {...(index === 0 && { id: VERIFIED_PIN_ID })}
              autoComplete="new-password"
              key={index}
              inputMode="numeric"
              type="new-password"
              name={`pin-${index}`}
              value={numbers[index]}
              min={0}
              max={9}
              onFocus={() => setActiveIndex(index)}
              onChange={(event) => handleOnChange(event, index)}
              onKeyDown={(event) =>
                handleOnKeyDown(event as BackspaceKeyEvent, index)
              }
            />
          );
        })}
      </div>
      {hasError && <p className="pin-field__error-title">{error}</p>}
    </div>
  );
};

export default PinField;
